import React from 'react';
import {
  Stack,
  TableCell,
  TableRow,
  Box,
  Typography,
  Tooltip,
  Button,
} from '@fortress-technology-solutions/fortress-component-library/Atoms';
import { useFlags } from 'launchdarkly-react-client-sdk';
import DashesIfNullOrUndefined from '../../components/DashesIfNullOrUndefined';
import { useEditHouseholdMemberDatesEnabled } from '../ResidentProfile/hooks';

import messages from './messages';
import ServiceAnimalIndicator from './ServiceAnimalIndicator';
import { HouseholdSection } from '@fortress-technology-solutions/fortress-component-library/Organisms_Fortress';
import { formatDateDisplayUTC } from '@fortress-technology-solutions/fortress-component-library/utils/index';
import changeHeadOfHouseholdMessages from './ChangeHeadOfHousehold/messages';

// Matches the headers array below -- the trailing action row spans all of them.
const HOUSEHOLD_TABLE_COLUMN_COUNT = 5;

type Props = {
  editHouseholdMember: Function,
  householdMembers: Array<Object>,
  mainCustomer?: Object,
  showChangeHeadOfHouseholdButton?: boolean,
  isChangeHeadOfHouseholdDisabled?: boolean,
  hasOpenCertsBlockingChangeHeadOfHousehold?: boolean,
  onOpenChangeHeadOfHousehold?: Function,
};

const ContactInformationDisplay = ({ customer, intl }) => {
  const { addlContactInfoResidents } = useFlags();

  // Only show enhanced contact info if feature flag is enabled and contact data exists
  if (!addlContactInfoResidents || !customer?.contact) {
    return null;
  }

  const { contact } = customer;
  const emails = contact?.emails || [];
  const phones = contact?.phones || [];

  return (
    <Box mt={2}>
      <Typography variant="labelMedium" gutterBottom component="div">
        Contact Information
      </Typography>

      {phones.length > 0 && (
        <Box mb={1}>
          <Typography variant="subtitle2">Phone Numbers:</Typography>
          {phones.map((phone, index) => (
            <Typography key={index} variant="titleSmall" component="div">
              {phone.phoneNumber} ({phone.phoneType})
              {phone.isPrimary && ' - Primary'}
            </Typography>
          ))}
        </Box>
      )}

      {emails.length > 0 && (
        <Box mb={1}>
          <Typography variant="subtitle2">Email Addresses:</Typography>
          {emails.map((email, index) => (
            <Typography key={index} variant="titleSmall" component="div">
              {email.emailAddress} ({email.emailType})
              {email.isPrimary && ' - Primary'}
            </Typography>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default function ResidentHousehold({
  intl,
  householdMembers,
  editHouseholdMember,
  mainCustomer,
  showChangeHeadOfHouseholdButton,
  isChangeHeadOfHouseholdDisabled,
  hasOpenCertsBlockingChangeHeadOfHousehold,
  onOpenChangeHeadOfHousehold,
}: Props) {
  const isEditHouseholdMemberDatesEnabled =
    useEditHouseholdMemberDatesEnabled();

  // Ticket requirement: the Head of Household is always identified first in
  // the list, not wherever it happens to land in fetch/creation order --
  // this matters most right after a Change Head of Household swap, since
  // the newly-promoted member otherwise stays wherever they already were.
  // Array#sort is stable, so this only moves the primary member and leaves
  // everyone else's relative order untouched.
  // Exclude all pet statuses when the separate Pets section is enabled.
  // Filter before sorting to preserve member order.
  const visibleHouseholdMembers = isEditHouseholdMemberDatesEnabled
    ? householdMembers.filter((occupant) => occupant.type !== 'pet')
    : householdMembers;

  const sortedHouseholdMembers = [...visibleHouseholdMembers].sort(
    (a, b) => Number(Boolean(b.isPrimary)) - Number(Boolean(a.isPrimary)),
  );

  const rows = sortedHouseholdMembers.map((occupant) => {
    const isPrior = occupant.status === 'Prior';
    const isPending = occupant.status === 'Pending';
    const isDenied = occupant.status === 'Denied';
    const isPet = occupant.type === 'pet';

    let color = isPrior ? 'text.secondary' : null;
    color = isPending ? 'error.main' : color;
    // Default: only Current members are clickable.
    let isClickable = !color && !isDenied && !isPet;

    // Behind the flag, Prior and Pending become clickable too (same
    // Mini-Profile as Current), with updated colors matching Figma.
    if (isEditHouseholdMemberDatesEnabled) {
      if (isPrior) {
        color = 'primary.light';
        isClickable = true;
      } else if (isPending) {
        color = 'text.secondary';
        isClickable = true;
      }
    }

    return (
      <TableRow
        key={occupant.id}
        sx={{ color, backgroundColor: 'transparent !important' }}
      >
        <TableCell>
          {' '}
          {isClickable ? (
            // Anchors have their own default link color that doesn't
            // inherit from an ancestor's `color` — the TableRow's `sx`
            // above works for the plain <span> below (which does
            // inherit), but a plain <a> here would always render the
            // browser/theme's default link blue regardless of `color`.
            // Box+component="a" applies `color` directly on the
            // anchor itself instead of relying on inheritance.
            <Box
              component="a"
              onClick={() => editHouseholdMember(occupant.id)}
              sx={{ color, cursor: 'pointer' }}
            >
              <DashesIfNullOrUndefined data={occupant.name} />
            </Box>
          ) : (
            <span>{occupant.name}</span>
          )}
        </TableCell>
        <TableCell>
          <Stack direction={'row'} spacing={0.5} alignItems={'center'}>
            <span>{occupant.relationship}</span>

            {occupant.isServiceAnimal && <ServiceAnimalIndicator />}
          </Stack>
        </TableCell>
        <TableCell>
          {occupant?.wasMinor ? (
            <Tooltip
              title={intl.formatMessage(messages.wasMinor)}
              arrow
              placement="top"
            >
              {occupant.status}
            </Tooltip>
          ) : (
            occupant.status
          )}
        </TableCell>
        <TableCell>
          {occupant.startDate && formatDateDisplayUTC(occupant.startDate)}
        </TableCell>
        <TableCell>
          {occupant.endDate && formatDateDisplayUTC(occupant.endDate)}
        </TableCell>
      </TableRow>
    );
  });

  // Rendered as a trailing table row (rather than after <HouseholdSection>)
  // so it lands before that component's own closing Divider, matching the
  // Figma layout of button-then-divider instead of divider-then-button.
  if (showChangeHeadOfHouseholdButton) {
    rows.push(
      <TableRow
        key="change-head-of-household"
        sx={{ backgroundColor: 'transparent !important' }}
      >
        <TableCell colSpan={HOUSEHOLD_TABLE_COLUMN_COUNT}>
          <Tooltip
            placement="top"
            // The library's Tooltip wraps children in a `width: fit-content`
            // Box, which would otherwise collapse our fullWidth Button back
            // down to its text width.
            containerSX={{ width: '100%' }}
            title={
              isChangeHeadOfHouseholdDisabled
                ? intl.formatMessage(
                    hasOpenCertsBlockingChangeHeadOfHousehold
                      ? changeHeadOfHouseholdMessages.openCertsTooltip
                      : changeHeadOfHouseholdMessages.noOtherAdultAvailableTooltip,
                  )
                : ''
            }
          >
            <Button
              variant="primarySubtle"
              fullWidth
              onClick={onOpenChangeHeadOfHousehold}
              disabled={isChangeHeadOfHouseholdDisabled}
              data-testid="change-head-of-household-button"
            >
              {intl.formatMessage(
                changeHeadOfHouseholdMessages.changeHeadOfHousehold,
              )}
            </Button>
          </Tooltip>
        </TableCell>
      </TableRow>,
    );
  }

  return (
    <>
      <HouseholdSection
        intl={intl}
        headers={[
          messages.name,
          messages.relation,
          messages.status,
          messages.startDate,
          messages.endDate,
        ]}
        rows={rows}
      />
      <ContactInformationDisplay customer={mainCustomer} intl={intl} />
    </>
  );
}
